import { getSmartCollection } from 'api/product/getSmartCollection';
import { SmartCollectionItem, SmartCollectionResponse } from 'api/product/getSmartCollection/types';
import { MIN_ITEMS_IN_CROSS_SELL_RESULT } from 'constants/cross-sell';
import { useCart } from 'contexts/cart';
import { useCrossSellSnacks } from 'hooks/product/use-cross-sell-snacks';
import { useIsPetProfileExist } from 'hooks/product/useIsPetProfileExist';
import { isProductIdInCart } from 'hooks/product/utils';
import { InputProductType } from 'interfaces/api-input/input-product-type';
import { shuffleItems } from 'modules/cart/components/upsell-product/utils';
import { useMemo } from 'react';
import { useSavingPlanStore } from 'stores/saving-plan';
import { useRootStore } from 'stores/use-root-store';
import useSWR from 'swr';
import { generateFallbackInputFromPetProfile } from 'utils/api/smartCollection';
import { isPinnedUpsellProduct } from 'utils/product/is-pinned-upsell-product';

const MAX_ITEMS_IN_RESULT = 5;

interface UseUpsellProductOutput<T = SmartCollectionItem> {
  error: Error | undefined;
  isFallback: boolean;
  isLoading: boolean;
  rawResponse: SmartCollectionResponse | null | undefined;
  dislikedProteinTags: ReturnType<
    typeof generateFallbackInputFromPetProfile
  >['disliked_protein_tags'];
  upsellProducts: NonNullable<T>[]; // NonNullable since we do check already inside of resultProducts reduce
}

interface UseUpsellProducts<T = SmartCollectionItem> {
  contentfulHandle?: string;
  fallbackSlug?: string;
  filterOutCartItems?: boolean;
  foodTypes?: InputProductType[];
  maxItemsInResult?: number;
  skip?: boolean; // if true skip any requests, both smart and static collection
  skipFallback?: boolean; // if true skip fallback requests
  converter?: (product: SmartCollectionItem) => T;
}

/**
 * Type overload to return correct type when converter function is provided
 */
export function useUpsellProducts(
  args: Omit<UseUpsellProducts, 'converter'> & {
    converter?: undefined;
  }
): UseUpsellProductOutput;

export function useUpsellProducts<T>(
  args: UseUpsellProducts<T> & {
    converter: (product: SmartCollectionItem) => T;
  }
): UseUpsellProductOutput<T>;

/**
 * Hook for collection-based Upsell products management.
 * Data is fetched based on the pet type of the products in the cart.
 */
export function useUpsellProducts<T = SmartCollectionItem>({
  fallbackSlug,
  filterOutCartItems = false,
  foodTypes,
  maxItemsInResult = MAX_ITEMS_IN_RESULT,
  skip = false,
  skipFallback = false,
  converter,
}: UseUpsellProducts<T>): UseUpsellProductOutput<T | SmartCollectionItem> {
  const { items: cartItems } = useCart();
  const activePlan = useSavingPlanStore((state) => state.activePlan);
  const petProfile = useRootStore((state) => state.currentPetProfile);
  const petProfileExists = useIsPetProfileExist();

  // fetches the smart collection with a fallback
  const {
    error: crossSellError,
    data: snacks,
    rawResponse,
    dislikedProteinTags,
    includeDislikedProteins,
    isPickyCatsAssignmentPending,
    isLoading,
  } = useCrossSellSnacks({
    skip,
    foodTypes,
  });
  const fallbackInput = fallbackSlug
    ? generateFallbackInputFromPetProfile({
        petProfile,
        includeDislikedProteins,
        slug: fallbackSlug,
        foodTypes: foodTypes ?? [InputProductType.Snacks, InputProductType.FoodSupplement],
      })
    : undefined;
  const isFetchComplete = snacks !== undefined && crossSellError === undefined && !isLoading;

  const availableSnacks = useMemo(() => snacks?.filter((item) => item.available), [snacks]);

  const upsellProductsIsEmpty =
    !availableSnacks ||
    (availableSnacks.length <= MIN_ITEMS_IN_CROSS_SELL_RESULT && isFetchComplete);
  const shouldFallback = !petProfileExists || upsellProductsIsEmpty;

  const {
    data: fallbackData,
    error,
    isLoading: isFallbackLoading,
  } = useSWR(
    // skip when skip or skipFallback is true, or should fall back and fallbackSlug does not exist
    skip || skipFallback || isPickyCatsAssignmentPending || !(shouldFallback && fallbackSlug)
      ? null
      : [
          `Upsell-Product-${fallbackSlug}-${activePlan?.ruleId ?? ''}`,
          ...(dislikedProteinTags ? [dislikedProteinTags.join(',')] : []),
        ].join('-'),
    async () => {
      if (fallbackInput) {
        return await getSmartCollection(fallbackInput, activePlan);
      }
    }
  );

  const availableFallbackItems = useMemo(
    () => fallbackData?.items.filter((item) => item.available),
    [fallbackData?.items]
  );

  // pinned items bypass the shuffle so a campaign product keeps the first slot on every revalidation
  const orderedItems = useMemo(() => {
    const result = (shouldFallback ? availableFallbackItems : availableSnacks) ?? [];
    const pinnedItems = result.filter((item) => isPinnedUpsellProduct(item.product_id));
    const restItems = result.filter((item) => !isPinnedUpsellProduct(item.product_id));

    return [...pinnedItems, ...shuffleItems(restItems)];
  }, [availableFallbackItems, availableSnacks, shouldFallback]);

  // limits result of the items and filters out the ones that in the cart already
  const filteredItems = useMemo<SmartCollectionItem[]>(() => {
    const filteredItems = filterOutCartItems
      ? orderedItems.filter((item) => !isProductIdInCart({ id: item.product_id, cartItems }))
      : orderedItems;

    return filteredItems.slice(0, maxItemsInResult);
  }, [cartItems, filterOutCartItems, maxItemsInResult, orderedItems]);

  // if there is a converter function provided it would convert smart items to the target converter function type
  const resultProducts = useMemo(() => {
    if (converter) {
      return filteredItems.reduce<NonNullable<T>[]>((acc, product) => {
        const converted = converter(product);
        if (converted) {
          acc.push(converted);
        }
        return acc;
      }, []);
    }
    return filteredItems;
  }, [converter, filteredItems]);

  return {
    error,
    isFallback: shouldFallback && !!fallbackData,
    isLoading: isFallbackLoading || isLoading || isPickyCatsAssignmentPending,
    rawResponse: rawResponse || fallbackData,
    dislikedProteinTags,
    upsellProducts: resultProducts,
  };
}
